import React, { Component } from 'react';
import classNames from 'classnames';

import { FormattedMessage, injectIntl } from '../../../util/reactIntl';
import { userDisplayNameAsString } from '../../../util/data';
import { isMobileSafari } from '../../../util/userAgent';
import { apiBaseUrl } from '../../../util/api';
import { createSlug } from '../../../util/urlHelpers';
import { displayPrice } from '../../../util/configHelpers';
import { formatMoney } from '../../../util/currency';
import {
  extractVariantTitle,
  getReturnTrackingInfo,
  getShippingExtendedDetails,
  getTrackingDetails,
  isRentalListing,
} from '../../../util/dataExtractors';
import { types as sdkTypes } from '../../../util/sdkLoader';
import {
  TRANSITION_RENTAL_PRODUCT_RECEIVED,
  TRANSITION_RENTAL_PRODUCT_RECEIVED_PAYPAL,
} from '../../../util/transactionLegacy';

import {
  ExternalLink,
  Icons,
  InlineTextButton,
  NamedLink,
  UserDisplayName,
} from '../../../components';

import SendMessageForm from '../SendMessageForm/SendMessageForm';
import TextMaybe from '../TextMaybe/TextMaybe';
import Print from '../Print';

// These are internal components that make this file more readable.
import BreakdownMaybe from './BreakdownMaybe';
import DeliveryInfoMaybe from './DeliveryInfoMaybe';
import BookingLocationMaybe from './BookingLocationMaybe';
import FeedSection from './FeedSection';
import PanelHeading from './PanelHeading';
import { getsOrderUpdatesByText } from '../../../components/EmailVerificationBanner/emailVerificationBanner.helpers';
import { getShippingLabelActionState } from './shippingLabelAction';
import ShipOrderSection from './ShipOrderSection';

import css from './TransactionPanel.module.css';

const { Money } = sdkTypes;

// Helper function to get display names for different roles
const displayNames = (currentUser, provider, customer, intl) => {
  const authorDisplayName = <UserDisplayName user={provider} intl={intl} />;
  const customerDisplayName = <UserDisplayName user={customer} intl={intl} />;

  let otherUserDisplayName = '';
  let otherUserDisplayNameString = '';
  const currentUserIsCustomer =
    currentUser?.id && customer?.id && currentUser.id.uuid === customer?.id?.uuid;
  const currentUserIsProvider =
    currentUser?.id && provider?.id && currentUser.id.uuid === provider?.id?.uuid;

  if (currentUserIsCustomer) {
    otherUserDisplayName = authorDisplayName;
    otherUserDisplayNameString = userDisplayNameAsString(provider, '');
  } else if (currentUserIsProvider) {
    otherUserDisplayName = customerDisplayName;
    otherUserDisplayNameString = userDisplayNameAsString(customer, '');
  }

  return {
    authorDisplayName,
    customerDisplayName,
    otherUserDisplayName,
    otherUserDisplayNameString,
  };
};

const allowShowingExtraInfo = (showExtraInfo, transactionPartyInfo) => {
  const {
    isCustomer,
    isCustomerBanned,
    isCustomerDeleted,
    isProvider,
    isProviderBanned,
    isProviderDeleted,
  } = transactionPartyInfo;
  return (
    !!showExtraInfo &&
    ((isProvider && !isCustomerBanned && !isCustomerDeleted) ||
      (isCustomer && !isProviderBanned && !isProviderDeleted))
  );
};

const parseCartItems = cartItemsRaw => {
  if (Array.isArray(cartItemsRaw)) {
    return cartItemsRaw;
  }
  if (typeof cartItemsRaw === 'string') {
    try {
      const parsed = JSON.parse(cartItemsRaw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }
  return [];
};

const getImageUrlFromVariants = variants => {
  if (!variants || typeof variants !== 'object') {
    return null;
  }
  return (
    variants['listing-card']?.url ||
    variants['square-small']?.url ||
    variants['square-small2x']?.url ||
    Object.values(variants).find(v => v?.url)?.url ||
    null
  );
};

const getImageUrlFromCartItem = item => {
  if (!item) {
    return null;
  }
  if (typeof item.firstImage === 'string') {
    return item.firstImage;
  }
  return getImageUrlFromVariants(item?.firstImage?.attributes?.variants);
};

const toMoney = value => {
  if (!value || typeof value !== 'object') {
    return null;
  }
  if (value instanceof Money) {
    return value;
  }
  if (typeof value.amount === 'number' && typeof value.currency === 'string') {
    return new Money(value.amount, value.currency);
  }
  return null;
};

const getQuantity = item => {
  const parsed = Number.parseInt(item?.stockCount ?? item?.quantity, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
};

const getFormattedItemPrice = (intl, unitPrice, quantity) => {
  const money = toMoney(unitPrice);
  if (!money) {
    return null;
  }

  const safeQuantity = Number.isInteger(quantity) && quantity > 0 ? quantity : 1;
  const unitAmount = typeof money.amount === 'number' ? money.amount : null;
  const totalMoney =
    unitAmount !== null && safeQuantity > 1
      ? new Money(unitAmount * safeQuantity, money.currency)
      : money;

  try {
    return formatMoney(intl, totalMoney);
  } catch (error) {
    return null;
  }
};

const formatOrderDate = (intl, dateValue) => {
  if (!dateValue) {
    return null;
  }
  const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
  return Number.isNaN(date.getTime())
    ? null
    : intl.formatDate(date, { month: 'short', day: 'numeric', year: 'numeric' });
};

const getTransactionPanelLabels = intl => {
  return {
    trackingDetails: intl.formatMessage({ id: 'TransactionPanel.paymentMessage' }),
    returnTrackingDetails: intl.formatMessage({ id: 'TransactionPanel.returnDetailsMessage' }),
    needHelp: intl.formatMessage({ id: 'TransactionPanel.needHelpMessage' }),
    orderIssues: intl.formatMessage({ id: 'TransactionPanel.orderIssuesMessage' }),
    deliveryInfo: intl.formatMessage({ id: 'TransactionPanel.deliveryInfoMessage' }),
    orderSummary: intl.formatMessage({ id: 'TransactionPanel.orderSummaryMessage' }),
  };
};

const formatPackingSlipDate = dateValue => {
  if (!dateValue) {
    return '';
  }
  const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const normalizeAddress = rawAddress => {
  if (!rawAddress || typeof rawAddress !== 'object') {
    return null;
  }

  const line1 = rawAddress?.line1 || rawAddress?.street1 || rawAddress?.addressLine1 || '';
  const line2 = rawAddress?.line2 || rawAddress?.street2 || rawAddress?.addressLine2 || '';
  const city = rawAddress?.city || '';
  const state = rawAddress?.state || '';
  const postalCode = rawAddress?.postalCode || rawAddress?.postal || rawAddress?.zip || '';
  const country = rawAddress?.country || rawAddress?.countryCode || '';

  const hasContent = [line1, line2, city, state, postalCode, country].some(Boolean);
  if (!hasContent) {
    return null;
  }

  return {
    line1,
    line2,
    city,
    state,
    postalCode,
    country,
  };
};

const getPackingSlipAddress = ({
  transaction,
  protectedData,
  provider,
  shouldShowPrintLabelCustomer,
}) => {
  const metadata = transaction?.attributes?.metadata;
  const providerShippingAddress = normalizeAddress(
    provider?.attributes?.profile?.protectedData?.shippingAddress
  );
  const addressCandidates = [
    protectedData?.shippingDetails?.shippingDetails?.address,
    protectedData?.shippingDetails?.address,
    protectedData?.shippingAddress,
    protectedData?.buyerInfo?.address,
    metadata?.shippingDetails?.address,
    metadata?.shippingAddress,
    protectedData?.shippingDetails,
  ];

  const customerAddress = addressCandidates.map(normalizeAddress).find(Boolean);
  const chosenAddress =
    shouldShowPrintLabelCustomer && providerShippingAddress
      ? providerShippingAddress
      : customerAddress;

  return (
    chosenAddress || {
      line1: '',
      line2: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
    }
  );
};

const getPackingSlipItemSize = item => {
  const sizeSources = [
    item?.selectedSizeLabel,
    item?.sizeLabel,
    item?.selectedSize,
    item?.size,
    item?.sizeOption?.label,
    item?.sizeOption?.values,
    item?.publicData?.sizeLabel,
    item?.publicData?.sizeOption?.label,
    item?.publicData?.sizeOption?.values,
    item?.publicData?.size,
  ];

  const size = sizeSources.find(value => {
    if (typeof value === 'string') {
      return value.trim().length > 0;
    }
    return !!value;
  });

  if (typeof size === 'string') {
    return size;
  }
  if (size && typeof size === 'object') {
    return size?.label || size?.values || null;
  }

  return null;
};

const getPackingSlipItems = ({ items, fallbackImageUrl, listingTitle }) => {
  const safeItems = Array.isArray(items) ? items : [];

  return safeItems.map(item => {
    const quantity = getQuantity(item);
    const rawTitle = item?.title || listingTitle || '';

    return {
      listingId: item?.listingId || null,
      firstImage: getImageUrlFromCartItem(item) || fallbackImageUrl || '',
      quantity,
      title: rawTitle,
      sku: item?.sku || item?.publicData?.sku || null,
      size: getPackingSlipItemSize(item),
    };
  });
};

/**
 * Transaction panel
 *
 * @component
 * @param {Object} props - The props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {propTypes.currentUser} props.currentUser - The current user
 * @param {string} props.transactionRole - The transaction role
 * @param {propTypes.listing} props.listing - The listing
 * @param {propTypes.user} props.customer - The customer
 * @param {propTypes.user} props.provider - The provider
 * @param {boolean} props.hasTransitions - Whether the transitions are shown
 * @param {propTypes.uuid} props.transactionId - The transaction id
 * @param {Array<propTypes.message>)} props.messages - The messages
 * @param {boolean} props.initialMessageFailed - Whether the initial message failed
 * @param {boolean} props.savePaymentMethodFailed - Whether the save payment method failed
 * @param {propTypes.error} props.fetchMessagesError - The fetch messages error
 * @param {boolean} props.sendMessageInProgress - Whether the send message is in progress
 * @param {propTypes.error} props.sendMessageError - The send message error
 * @param {Function} props.onOpenDisputeModal - The on open dispute modal function
 * @param {Function} [props.onOpenCancelModal] - Opens the seller's cancel order modal
 * @param {Function} props.onSendMessage - The on send message function
 * @param {stateDataShape} props.stateData - The state data
 * @param {boolean} props.showBookingLocation - Whether the booking location is shown
 * @param {React.ReactNode} props.activityFeed - The activity feed
 * @param {Function} props.actionButtons - The action buttons function
 * @param {React.ReactNode} props.orderBreakdown - The order breakdown
 * @param {React.ReactNode} props.orderPanel - The order panel
 * @param {object} props.config - The config
 * @param {intlShape} props.intl - The intl
 * @returns {JSX.Element} The TransactionPanel component
 */
export class TransactionPanelComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sendMessageFormFocused: false,
    };
    this.isMobSaf = false;
    this.sendMessageFormName = 'TransactionPanel.SendMessageForm';

    this.onSendMessageFormFocus = this.onSendMessageFormFocus.bind(this);
    this.onSendMessageFormBlur = this.onSendMessageFormBlur.bind(this);
    this.onMessageSubmit = this.onMessageSubmit.bind(this);
    this.scrollToMessage = this.scrollToMessage.bind(this);
  }

  componentDidMount() {
    this.isMobSaf = isMobileSafari();
  }

  onSendMessageFormFocus() {
    this.setState({ sendMessageFormFocused: true });
    if (this.isMobSaf) {
      // Scroll to bottom
      window.scroll({ top: document.body.scrollHeight, left: 0, behavior: 'smooth' });
    }
  }

  onSendMessageFormBlur() {
    this.setState({ sendMessageFormFocused: false });
  }

  onMessageSubmit(values, form) {
    const message = values.message ? values.message.trim() : null;
    const { transactionId, onSendMessage, config } = this.props;

    if (!message) {
      return;
    }
    return onSendMessage(transactionId, message, config)
      .then(messageId => {
        form.reset();
        this.scrollToMessage(messageId);
      });
  }

  scrollToMessage(messageId) {
    const selector = `#msg-${messageId.uuid}`;
    const el = document.querySelector(selector);
    if (el) {
      el.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    }
  }

  render() {
    const {
      rootClassName,
      className,
      currentUser,
      transactionRole,
      transactionId,
      listing,
      customer,
      provider,
      transitions,
      protectedData,
      messages,
      initialMessageFailed = false,
      savePaymentMethodFailed = false,
      fetchMessagesError,
      sendMessageInProgress,
      sendMessageError,
      onOpenDisputeModal,
      onOpenCancelModal,
      onManageDisableScrolling,
      intl,
      stateData = {},
      showBookingLocation = false,
      transaction,
      requestQuote,
      offer,
      activityFeed,
      actionButtons,
      actionButtonContainer = 'desktop',
      isInquiryProcess,
      orderBreakdown,
      orderPanel,
      config,
      hasViewingRights,
      isOwnShipping = false,
      isTutulistSellerShipping = false,
      fetchedShippingDetails,
      createShippoLabelInProgress = false,
      createShippoLabelError = null,
      updateShippingAddressInProgress = false,
      updateShippingAddressError = null,
      onRetryShippoLabelWithAddress,
      onRetryShippoLabel,
      onRefreshCurrentUser,
    } = this.props;

    const transitionList = Array.isArray(transitions) ? transitions : [];
    const messageList = Array.isArray(messages) ? messages : [];
    const hasTransitions = transitionList.length > 0;
    const isCustomer = transactionRole === 'customer';
    const isProvider = transactionRole === 'provider';

    const listingDeleted = !!listing?.attributes?.deleted;
    const isCustomerBanned = !!customer?.attributes?.banned;
    const isCustomerDeleted = !!customer?.attributes?.deleted;
    const isProviderBanned = !!provider?.attributes?.banned;
    const isProviderDeleted = !!provider?.attributes?.deleted;

    const transactionPartyInfo = {
      isCustomer,
      isCustomerBanned,
      isCustomerDeleted,
      isProvider,
      isProviderBanned,
      isProviderDeleted,
    };

    const { authorDisplayName, customerDisplayName, otherUserDisplayNameString } = displayNames(
      currentUser,
      provider,
      customer,
      intl
    );

    const deletedListingTitle = intl.formatMessage({
      id: 'TransactionPanel.deletedListingTitle',
    });

    const listingTitle = listingDeleted ? deletedListingTitle : listing?.attributes?.title;
    const firstImage = listing?.images?.length > 0 ? listing?.images[0] : null;

    const listingType = listing?.attributes?.publicData?.listingType;
    const listingTypeConfigs = config.listing.listingTypes;
    const listingTypeConfig = listingTypeConfigs.find(conf => conf.listingType === listingType);
    const showPrice = isInquiryProcess && displayPrice(listingTypeConfig);
    const showBreakDown = stateData.showBreakDown !== false; // NOTE: undefined defaults to true due to historical reasons.
    const showOrderSummary = showBreakDown && !!orderBreakdown;

    const showSendMessageForm =
      !isCustomerBanned && !isCustomerDeleted && !isProviderBanned && !isProviderDeleted;

    // Only show order panel for users who have listing viewing rights, otherwise
    // show the detail card heading.
    const showOrderPanel = stateData.showOrderPanel && hasViewingRights;
    const showDetailCardHeadings = stateData.showDetailCardHeadings || !hasViewingRights;
    const showItemsSection = showDetailCardHeadings && !!listing;

    const deliveryMethod = protectedData?.deliveryMethod || 'none';
    const priceVariantName = protectedData?.priceVariantName;

    const inquiryMessage = !isCustomerBanned
      ? protectedData?.inquiryMessage
      : intl.formatMessage({ id: 'TransactionPage.messageSenderBanned' });

    const classes = classNames(rootClassName || css.root, className);
    const orderId = transactionId?.uuid || transaction?.id?.uuid || null;
    const orderIdMessage = orderId
      ? intl.formatMessage({ id: 'TransactionPanel.orderIdMessage' }, { orderId })
      : null;
    const orderDate = formatOrderDate(intl, transaction?.attributes?.createdAt);

    const parsedCartItems = parseCartItems(protectedData?.cartItems);
    const fallbackCartItem =
      listing?.id?.uuid && listingTitle
        ? {
            listingId: listing.id.uuid,
            title: listingTitle,
            stockCount: 1,
            price: listing?.attributes?.price,
            firstImage,
            sku: listing?.attributes?.publicData?.sku || null,
            sizeLabel:
              listing?.attributes?.publicData?.sizeOption?.label ||
              listing?.attributes?.publicData?.sizeLabel ||
              listing?.attributes?.publicData?.size ||
              null,
          }
        : null;
    const items = parsedCartItems.length
      ? parsedCartItems
      : fallbackCartItem
      ? [fallbackCartItem]
      : [];
    const fallbackImageUrl = getImageUrlFromVariants(firstImage?.attributes?.variants);
    const isRental = isRentalListing(listing);
    const shouldShowPrintLabelCustomer =
      [TRANSITION_RENTAL_PRODUCT_RECEIVED, TRANSITION_RENTAL_PRODUCT_RECEIVED_PAYPAL].includes(
        transaction?.attributes?.lastTransition
      ) && isCustomer;
    const shouldShowPackingSlip =
      stateData.showActionButtons && ((isProvider && !isRental) || shouldShowPrintLabelCustomer);
    const providerName =
      provider?.attributes?.profile?.publicData?.storeName || userDisplayNameAsString(provider, '');
    const recipientNameFromShipping =
      protectedData?.shippingDetails?.shippingDetails?.name ||
      protectedData?.shippingDetails?.name ||
      transaction?.attributes?.metadata?.shippingDetails?.name ||
      protectedData?.buyerInfo?.name ||
      userDisplayNameAsString(customer, '');
    const packingSlipBuyerName = shouldShowPrintLabelCustomer
      ? userDisplayNameAsString(provider, '')
      : recipientNameFromShipping;
    const packingSlipAddress = getPackingSlipAddress({
      transaction,
      protectedData,
      provider,
      shouldShowPrintLabelCustomer,
    });
    const packingSlipDate = formatPackingSlipDate(transaction?.attributes?.createdAt);
    const packingSlipItems = getPackingSlipItems({
      items,
      fallbackImageUrl,
      listingTitle,
    });

    const metadataTrackingInfo = transaction?.attributes?.metadata?.trackingInfo;
    const trackingDetails = getTrackingDetails(transaction);
    const returnTrackingInfo = getReturnTrackingInfo(transaction);
    const extendedShippingDetails = getShippingExtendedDetails(transaction);
    const carrierName =
      trackingDetails?.carrierName ||
      extendedShippingDetails?.name ||
      extendedShippingDetails?.carrier ||
      extendedShippingDetails?.provider ||
      protectedData?.carrier ||
      protectedData?.shippingDetails?.moreDetails?.shippingInfo?.carrier ||
      protectedData?.shippingDetails?.moreDetails?.shippingInfo?.carrierName ||
      protectedData?.shippingDetails?.moreDetails?.shippingInfo?.provider ||
      protectedData?.shippingDetails?.moreDetails?.shippingInfo?.name ||
      null;
    const trackingNumber = trackingDetails?.trackingNumber || protectedData?.trackingId || null;
    const trackingUrlProvider =
      trackingDetails?.trackingUrlProvider ||
      metadataTrackingInfo?.tracking_url_provider ||
      metadataTrackingInfo?.trackingUrlProvider ||
      null;
    const hasTrackingDetails = Boolean(carrierName || trackingNumber || trackingUrlProvider);
    const showOwnShippingTrackingPendingMessage = isOwnShipping && !hasTrackingDetails;

    const returnCarrierName =
      returnTrackingInfo?.rate?.provider || returnTrackingInfo?.carrier || null;
    const returnTrackingNumber =
      returnTrackingInfo?.trackingNumber || returnTrackingInfo?.tracking_number || null;
    const returnTrackingUrl = returnTrackingInfo?.tracking_url_provider || null;
    const hasReturnTracking = !!(returnCarrierName || returnTrackingNumber);
    const isMobileActions = actionButtonContainer === 'mobile';
    const {
      downloadLabelUrl,
      qrCodeUrl,
      showDownloadLabelLink,
      showQrLabelLink,
      showLabelUnavailableMessage,
    } = getShippingLabelActionState({
      transactionRole,
      trackingInfo: metadataTrackingInfo,
      returnTrackingInfo,
      protectedData,
      isMobileActions,
      labelLinkBase: orderId
        ? `${apiBaseUrl(config?.marketplaceRootURL)}/api/shippo/label-link/${orderId}`
        : null,
    });
    const downloadLabelMessage = intl.formatMessage({ id: 'TransactionPanel.labelOrder' });
    const showQrLabelMessage = intl.formatMessage({ id: 'TransactionPanel.showQRlabelOrder' });
    const trackOrderMessage = intl.formatMessage({ id: 'TransactionPanel.trackOrderMessage' });
    const disputeOrderMessage = intl.formatMessage({ id: 'TransactionPanel.disputeOrder' });
    const panelLabels = getTransactionPanelLabels(intl);

    const trackingCarrierMessage = carrierName
      ? intl.formatMessage(
          { id: 'TransactionPanel.trackingCarrierMessage' },
          { carrier: carrierName }
        )
      : null;
    const trackingNumberMessage = trackingNumber
      ? intl.formatMessage(
          { id: 'TransactionPanel.trackingNumber' },
          { trackingNumber: trackingNumber }
        )
      : null;
    const returnCarrierMessage = returnCarrierName
      ? intl.formatMessage(
          { id: 'TransactionPanel.trackingCarrierMessage' },
          { carrier: returnCarrierName }
        )
      : null;
    const returnTrackingNumberMessage = returnTrackingNumber
      ? intl.formatMessage(
          { id: 'TransactionPanel.trackingNumber' },
          { trackingNumber: returnTrackingNumber }
        )
      : null;

    const showTrackOrderLink =
      stateData.showActionButtons && !shouldShowPackingSlip && !isOwnShipping && !isRental;
    const showDisputeOrderLink =
      stateData.showActionButtons && !shouldShowPackingSlip && stateData.showDispute;
    const showCancelOrderLink =
      stateData.showActionButtons && stateData.showSellerCancel && !!onOpenCancelModal;
    const hasActionButtonContextLinks =
      showCancelOrderLink ||
      showDownloadLabelLink ||
      shouldShowPackingSlip ||
      showTrackOrderLink ||
      showDisputeOrderLink ||
      showQrLabelLink ||
      showLabelUnavailableMessage;

    const actionButtonsMaybeRaw =
      stateData.showActionButtons && actionButtons
        ? typeof actionButtons === 'function'
          ? actionButtons(actionButtonContainer)
          : actionButtons
        : null;
    const actionButtonContextLinks = (
      <>
        {/* First so it sits at the far left of the row */}
        {showCancelOrderLink ? (
          <InlineTextButton
            id={`${actionButtonContainer}_cancelOrderButton`}
            className={css.trackOrder}
            onClick={onOpenCancelModal}
          >
            <FormattedMessage id="TransactionPanel.cancelOrder" />
          </InlineTextButton>
        ) : null}
        {showDownloadLabelLink ? (
          <ExternalLink className={css.trackOrder} href={downloadLabelUrl}>
            {downloadLabelMessage}
          </ExternalLink>
        ) : null}
        {showLabelUnavailableMessage ? (
          <p className={css.labelUnavailableMessage}>
            <FormattedMessage id="TransactionPanel.shippingLabelUnavailable" />
          </p>
        ) : null}
        {shouldShowPackingSlip ? (
          <Print
            providerName={providerName}
            orderId={orderId}
            startDate={packingSlipDate}
            address={packingSlipAddress}
            buyerName={packingSlipBuyerName}
            savedCart={packingSlipItems}
            onManageDisableScrolling={onManageDisableScrolling}
          />
        ) : (
          <>
            {showTrackOrderLink ? (
              <NamedLink className={css.trackOrder} name="SaleTrackPage" params={{ id: orderId }}>
                {trackOrderMessage}
              </NamedLink>
            ) : null}
            {showDisputeOrderLink ? (
              <InlineTextButton
                id={`${actionButtonContainer}_disputeOrderButton`}
                className={css.trackOrder}
                onClick={onOpenDisputeModal}
              >
                {disputeOrderMessage}
              </InlineTextButton>
            ) : null}
          </>
        )}
        {showQrLabelLink ? (
          <ExternalLink className={css.trackOrder} href={qrCodeUrl}>
            {showQrLabelMessage}
          </ExternalLink>
        ) : null}
      </>
    );
    // A paid order waiting to ship gets the "Ship this order" steps, which hold the label,
    // packing slip and "I've shipped it" button instead of the row at the top
    const showShipOrderSection =
      isProvider &&
      stateData.processState === 'purchased' &&
      deliveryMethod === 'shipping' &&
      !isRental &&
      !!actionButtonsMaybeRaw;
    const shipButtons =
      showShipOrderSection && React.isValidElement(actionButtonsMaybeRaw)
        ? React.cloneElement(
            actionButtonsMaybeRaw,
            { rootClassName: css.actionButtons },
            showCancelOrderLink ? (
              <InlineTextButton
                id={`${actionButtonContainer}_cancelOrderButton`}
                className={css.trackOrder}
                onClick={onOpenCancelModal}
              >
                <FormattedMessage id="TransactionPanel.cancelOrder" />
              </InlineTextButton>
            ) : null
          )
        : null;
    const shipQrCodeUrl = showShipOrderSection
      ? getShippingLabelActionState({
          transactionRole,
          trackingInfo: metadataTrackingInfo,
          returnTrackingInfo,
          protectedData,
          isMobileActions: true,
          labelLinkBase: orderId
            ? `${apiBaseUrl(config?.marketplaceRootURL)}/api/shippo/label-link/${orderId}`
            : null,
        }).qrCodeUrl
      : null;
    const actionButtonsMaybe =
      actionButtonsMaybeRaw && React.isValidElement(actionButtonsMaybeRaw)
        ? React.cloneElement(
            actionButtonsMaybeRaw,
            { rootClassName: css.actionButtons },
            actionButtonContextLinks
          )
        : actionButtonsMaybeRaw;
    const contextLinksMaybe =
      !actionButtonsMaybe && hasActionButtonContextLinks ? (
        <div className={css.actionButtons}>{actionButtonContextLinks}</div>
      ) : null;
    const transactionHeadActions = showShipOrderSection
      ? null
      : actionButtonsMaybe || contextLinksMaybe;
    // Right after buying, the buyer can't get the receipt or shipping emails until they verify
    const showVerifyEmailNote =
      isCustomer &&
      stateData.processState === 'purchased' &&
      currentUser?.attributes?.emailVerified === false &&
      !getsOrderUpdatesByText(currentUser);

    return (
      <div className={classes}>
        <div className={css.transactionContainer}>
          <div className={css.transactionContent}>
            <PanelHeading
              className={css.headingText}
              processName={stateData.processName}
              processState={stateData.processState}
              showExtraInfo={allowShowingExtraInfo(stateData.showExtraInfo, transactionPartyInfo)}
              showPriceOnMobile={showPrice}
              price={listing?.attributes?.price}
              intl={intl}
              deliveryMethod={deliveryMethod}
              isPendingPayment={!!stateData.isPendingPayment}
              statusTitle={stateData.statusTitle}
              transactionRole={transactionRole}
              providerName={authorDisplayName}
              customerName={customerDisplayName}
              listingId={listing?.id?.uuid}
              listingTitle={listingTitle}
              listingDeleted={listingDeleted}
            />

            {showVerifyEmailNote ? (
              <p className={css.verifyEmailNote}>
                <FormattedMessage
                  id="TransactionPanel.verifyEmailForUpdates"
                  values={{
                    email: <strong>{currentUser.attributes.email}</strong>,
                    link: (
                      <NamedLink name="ContactDetailsPage">
                        <FormattedMessage id="TransactionPanel.verifyEmailForUpdatesLink" />
                      </NamedLink>
                    ),
                  }}
                />
              </p>
            ) : null}

            <div className={css.txInfo}>
              <div className={css.transactionHead}>
                <div className={css.transactionHeadLeft}>
                  {orderIdMessage ? <h2>{orderIdMessage}</h2> : null}
                  {orderDate ? (
                    <p className={css.estimatedDelivery}>
                      <Icons name="trackingPlane" />
                      {orderDate}
                    </p>
                  ) : null}
                </div>
                <div className={css.transactionHeadRight}>
                  {transactionHeadActions ? <>{transactionHeadActions}</> : null}
                </div>
              </div>
              {showShipOrderSection ? (
                <ShipOrderSection
                  transaction={transaction}
                  items={packingSlipItems}
                  printProps={{
                    providerName,
                    orderId,
                    startDate: packingSlipDate,
                    address: packingSlipAddress,
                    buyerName: packingSlipBuyerName,
                    onManageDisableScrolling,
                  }}
                  isOwnShipping={isOwnShipping}
                  downloadLabelUrl={downloadLabelUrl}
                  qrCodeUrl={shipQrCodeUrl}
                  showLabelUnavailable={showLabelUnavailableMessage}
                  carrierName={carrierName}
                  serviceLevelName={
                    metadataTrackingInfo?.rate?.servicelevel?.name ||
                    protectedData?.shippingDetails?.moreDetails?.shippingInfo?.serviceLevel?.name ||
                    null
                  }
                  shipButtons={shipButtons}
                />
              ) : null}
              {showItemsSection ? (
                <div className={css.ItemsDetailSec}>
                  {items.map((item, index) => {
                    const itemTitleRaw = item?.title || listingTitle || '';
                    const itemTitle = extractVariantTitle(itemTitleRaw) || itemTitleRaw;
                    const itemListingId = item?.listingId || listing?.id?.uuid;
                    const quantity = getQuantity(item);
                    const imageUrl = getImageUrlFromCartItem(item) || fallbackImageUrl;
                    const formattedPrice = getFormattedItemPrice(
                      intl,
                      item?.price || listing?.attributes?.price,
                      quantity
                    );
                    const canLink = !!itemListingId && !listingDeleted;
                    const listingLinkParams = canLink
                      ? {
                          id: itemListingId,
                          slug: createSlug(itemTitleRaw || listingTitle || 'listing'),
                        }
                      : null;

                    return (
                      <div className={css.itemRow} key={`${itemListingId || itemTitle}-${index}`}>
                        <div className={css.itemImg}>
                          {imageUrl ? <img src={imageUrl} alt={itemTitle} /> : null}
                        </div>
                        <div className={css.itemInfo}>
                          <div className={css.itemInfoLeft}>
                            <h5>
                              {canLink ? (
                                <NamedLink name="ListingPage" params={listingLinkParams}>
                                  {itemTitle}
                                </NamedLink>
                              ) : (
                                itemTitle
                              )}
                            </h5>
                          </div>
                          <div className={css.itemInfoRight}>
                            {formattedPrice ? <h3>{formattedPrice}</h3> : null}
                            <p>
                              <FormattedMessage
                                id="TransactionPanel.quantityMessage"
                                values={{ quantity }}
                              />
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}
              <TextMaybe
                rootClassName={css.inquiryMessageContainer}
                heading={intl.formatMessage({ id: 'TransactionPanel.inquiryMessageHeading' })}
                text={inquiryMessage}
                isOwn={isCustomer}
                showText={isInquiryProcess}
              />

              {requestQuote}
              {offer}
              {/* {savePaymentMethodFailed ? (
                <p className={css.genericError}>
                  <FormattedMessage
                    id="TransactionPanel.savePaymentMethodFailed"
                    values={{
                      paymentMethodsPageLink: (
                        <NamedLink name="PaymentMethodsPage">
                          <FormattedMessage id="TransactionPanel.paymentMethodsPageLink" />
                        </NamedLink>
                      ),
                    }}
                  />
                </p>
              ) : null} */}

              <div className={css.paymentDeliverySection}>
                <div className={css.paymentDetails}>
                  {hasReturnTracking ? (
                    <>
                      <h2>{panelLabels.returnTrackingDetails}</h2>
                      {returnCarrierMessage ? <p>{returnCarrierMessage}</p> : null}
                      {returnTrackingNumberMessage ? <p>{returnTrackingNumberMessage}</p> : null}
                    </>
                  ) : null}
                  <h2>{panelLabels.trackingDetails}</h2>
                  {trackingCarrierMessage ? <p>{trackingCarrierMessage}</p> : null}
                  {trackingNumberMessage ? <p>{trackingNumberMessage}</p> : null}
                  {trackingUrlProvider ? (
                    <p>
                      <ExternalLink href={trackingUrlProvider}>
                        <span className={css.trackingLinkWrapper}>
                          <span>
                            {intl.formatMessage({ id: 'TransactionPanel.trackingUrlLabel' })}
                          </span>
                          <span className={css.iconContainer}>
                            <Icons name="trackingPlane" />
                          </span>
                        </span>
                      </ExternalLink>
                    </p>
                  ) : null}
                  {showOwnShippingTrackingPendingMessage ? (
                    <p className={css.trackingPendingMessage}>
                      <FormattedMessage id="TransactionPanel.ownShippingTrackingPendingLine1" />
                      <br />
                      <FormattedMessage id="TransactionPanel.ownShippingTrackingPendingLine2" />
                    </p>
                  ) : null}
                  <h2>{panelLabels.needHelp}</h2>
                  <NamedLink name="ContactDetailsPage">
                    <Icons name="orderIssues" />
                    {panelLabels.orderIssues}
                  </NamedLink>
                  {returnTrackingUrl || trackingUrlProvider ? (
                    <ExternalLink href={returnTrackingUrl || trackingUrlProvider}>
                      <Icons name="deliveryInfo" />
                      {panelLabels.deliveryInfo}
                    </ExternalLink>
                  ) : null}
                </div>

                {showBookingLocation ? (
                  <BookingLocationMaybe
                    className={css.deliveryInfoSection}
                    listing={listing}
                    showBookingLocation={showBookingLocation}
                  />
                ) : (
                  <DeliveryInfoMaybe
                    className={css.deliveryInfoSection}
                    transaction={transaction}
                    protectedData={protectedData}
                    listing={listing}
                    customer={customer}
                    currentUser={currentUser}
                    fetchedShippingDetails={fetchedShippingDetails}
                    transactionRole={transactionRole}
                    isTutulistSellerShipping={isTutulistSellerShipping}
                    createShippoLabelInProgress={createShippoLabelInProgress}
                    createShippoLabelError={createShippoLabelError}
                    updateShippingAddressInProgress={updateShippingAddressInProgress}
                    updateShippingAddressError={updateShippingAddressError}
                    onRetryShippoLabelWithAddress={onRetryShippoLabelWithAddress}
                    onRetryShippoLabel={onRetryShippoLabel}
                    onRefreshCurrentUser={onRefreshCurrentUser}
                    onManageDisableScrolling={onManageDisableScrolling}
                    locale={config.localization.locale}
                  />
                )}

                {showOrderPanel ? orderPanel : null}

                {showOrderSummary ? (
                  <div className={css.paymentDetails}>
                    <h2>{panelLabels.orderSummary}</h2>
                    <BreakdownMaybe
                      className={css.breakdownContainer}
                      orderBreakdown={orderBreakdown}
                      processName={stateData.processName}
                      priceVariantName={priceVariantName}
                      isTransactionBreakdown={true}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <div className={css.transactionChat}>
            <FeedSection
              rootClassName={css.feedContainer}
              hasMessages={messageList.length > 0}
              hasTransitions={hasTransitions}
              fetchMessagesError={fetchMessagesError}
              initialMessageFailed={initialMessageFailed}
              activityFeed={activityFeed}
              isConversation={isInquiryProcess}
            />
            {showSendMessageForm ? (
              <SendMessageForm
                formId={this.sendMessageFormName}
                rootClassName={css.sendMessageForm}
                messagePlaceholder={intl.formatMessage(
                  { id: 'TransactionPanel.sendMessagePlaceholder' },
                  { name: otherUserDisplayNameString }
                )}
                inProgress={sendMessageInProgress}
                sendMessageError={sendMessageError}
                onFocus={this.onSendMessageFormFocus}
                onBlur={this.onSendMessageFormBlur}
                onSubmit={this.onMessageSubmit}
              />
            ) : (
              <div className={css.sendingMessageNotAllowed}>
                <FormattedMessage id="TransactionPanel.sendingMessageNotAllowed" />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

const TransactionPanel = injectIntl(TransactionPanelComponent);

export default TransactionPanel;
